import React from 'react';
import { Box, TextField, Button, Typography, CircularProgress, Alert } from '@mui/material';
import { SmartToy as AIIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { isValidUrl } from '../../../lib/businessProfileUtils';

interface BusinessInfoGeneratorSectionProps {
  websiteUrl: string;
  onChange: (value: string) => void;
  onGenerate: () => void;
  generating: boolean;
  error: string;
  disabled?: boolean;
  quotaMessage?: string | null;
}

const BusinessInfoGeneratorSection: React.FC<BusinessInfoGeneratorSectionProps> = ({
  websiteUrl,
  onChange,
  onGenerate,
  generating,
  error,
  disabled = false,
  quotaMessage,
}) => {
  const { t } = useTranslation('profile');
  
  return (
    <Box>
      <TextField
        fullWidth
        label={t('businessProfile.basicInfo.websiteUrl')}
        value={websiteUrl}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('businessProfile.basicInfo.websitePlaceholder')}
      />
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={onGenerate}
            disabled={generating || disabled || !websiteUrl}
            startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <AIIcon />}
            sx={{
              background: 'linear-gradient(135deg, #6C63FF 0%, #FF6CAB 100%)',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(108, 99, 255, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5A52E6 0%, #E55A8A 100%)',
                boxShadow: '0 6px 16px rgba(108, 99, 255, 0.4)',
              },
              '&:disabled': {
                background: 'rgba(108, 99, 255, 0.5)',
                boxShadow: 'none',
              },
            }}
          >
            {generating ? t('businessProfile.aiExtract.extracting') : t('businessProfile.aiExtract.extractButton')}
          </Button>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            {t('businessProfile.aiExtract.extractDescription')}
          </Typography>
        </Box>
        {quotaMessage && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {quotaMessage}
          </Alert>
        )}
      </Box>
      {error && (
        <Alert key="generation-error" severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default BusinessInfoGeneratorSection;

